/**
 * Copyright (c) Microsoft. All rights reserved.
 */

/* eslint-disable no-labels */
import remove from 'lodash.remove';
import { doLabelsOverlap } from 'src/luis/modules/labeling/utils/generalUtils';
import {
    assignChildren,
    attachHierarchicalParentIds,
    buildEntityCollection,
    createAncestorLabels,
    cutLabel,
    EntityCollection,
    getSchemaPath,
    InternalLabel,
    LabelingInteractionsEntity,
    LabelingInteractionsEntityLabel,
    NEGATIVE_ENTITY_ID,
    shrinkLabel,
    toInteractionsLabel,
    toInternalLabel
} from 'src/luis/projects/utterances/handlers/labelingInteractionsUtils';

type LabelingInteractionsState = {
    entities: LabelingInteractionsEntity[];
    labels: LabelingInteractionsEntityLabel[];
};

type LabelingInteractionsReturn = { labels: LabelingInteractionsEntityLabel[] };
type RulesReturn = { addedLabels: InternalLabel[]; removedLabel: InternalLabel };

type AddLabelAction = { name: 'AddLabel'; label: LabelingInteractionsEntityLabel };
type RemoveLabelsAction = { name: 'RemoveLabels'; labels: LabelingInteractionsEntityLabel[] };
type ResizeLabelAction = { name: 'ResizeLabel'; label: LabelingInteractionsEntityLabel; newRange: [number, number] };
type ResizePredictionAction = { name: 'ResizePrediction'; label: LabelingInteractionsEntityLabel; newRange: [number, number] };
type ConfirmPredictionsAction = { name: 'ConfirmPredictions'; labels: LabelingInteractionsEntityLabel[] };

export type LabelingInteractionsAction =
    | AddLabelAction
    | ResizeLabelAction
    | RemoveLabelsAction
    | ResizePredictionAction
    | ConfirmPredictionsAction;

/**
 * This function is responsible for cutting the second label to accommodate for the
 * first label that must be applied.
 */
const firstMustBeAppliedRule = (firstLabel: InternalLabel, secondLabel: InternalLabel, shouldBorrowChildren = true): RulesReturn => {
    const addedLabels: InternalLabel[] = [];

    const [firstCut, overlappingCut, secondCut] = cutLabel(secondLabel, [firstLabel.originalLabel.start, firstLabel.originalLabel.end]);

    const firstCutLabel = shrinkLabel(secondLabel, firstCut[0], firstCut[1]);
    const secondCutLabel = shrinkLabel(secondLabel, secondCut[0], secondCut[1]);
    const overlappingCutLabel = shrinkLabel(secondLabel, overlappingCut[0], overlappingCut[1]);

    if (firstCutLabel) {
        addedLabels.push(firstCutLabel);
    }

    if (secondCutLabel) {
        addedLabels.push(secondCutLabel);
    }

    if (overlappingCutLabel && shouldBorrowChildren) {
        firstLabel.children.push(...overlappingCutLabel.children);
        applyRules(firstLabel.children, false);
    }

    return { addedLabels, removedLabel: secondLabel };
};

/**
 * Merges two labels of the same type, by getting the bigger subset of token indices,
 * and merging the children and run applyRules on them.
 */
const mergeLabelsRule = (firstLabel: InternalLabel, secondLabel: InternalLabel): RulesReturn => {
    const start = Math.min(firstLabel.originalLabel.start, secondLabel.originalLabel.start);
    const end = Math.max(firstLabel.originalLabel.end, secondLabel.originalLabel.end);

    // Keep the reference to the inferred label and delete the other.
    const mergeLabels = (inferredLabel: InternalLabel, existingLabel: InternalLabel): RulesReturn => {
        inferredLabel.originalLabel = {
            entityId: inferredLabel.originalLabel.entityId,
            roleId: inferredLabel.originalLabel.roleId,
            start,
            end
        };
        inferredLabel.children.push(...existingLabel.children);
        applyRules(inferredLabel.children, false);

        return { addedLabels: [], removedLabel: existingLabel };
    };

    if (firstLabel.kind === 'inferred') {
        return mergeLabels(firstLabel, secondLabel);
    }
    return mergeLabels(secondLabel, firstLabel);
};

/**
 * Overlapping rules for 1st level labels of the same entity type.
 */
const topLevelRules = (firstLabel: InternalLabel, secondLabel: InternalLabel): RulesReturn => {
    if (firstLabel.kind === 'userAction') {
        return firstMustBeAppliedRule(firstLabel, secondLabel, true);
    }

    if (secondLabel.kind === 'userAction') {
        return firstMustBeAppliedRule(secondLabel, firstLabel, true);
    }

    return mergeLabelsRule(firstLabel, secondLabel);
};

/**
 * Overlapping rules for 2nd level and upwards.
 */
const innerLevelsRules = (firstLabel: InternalLabel, secondLabel: InternalLabel): RulesReturn => {
    // If entity types are not the same, apply the userAction or inferred label
    if (firstLabel.originalLabel.entityId !== secondLabel.originalLabel.entityId) {
        if (firstLabel.kind === 'userAction' || firstLabel.kind === 'inferred') {
            return firstMustBeAppliedRule(firstLabel, secondLabel, false);
        }

        if (secondLabel.kind === 'userAction' || secondLabel.kind === 'inferred') {
            return firstMustBeAppliedRule(secondLabel, firstLabel, false);
        }
    } else {
        // if entity types are the same, apply the userAction label, if none
        // is userAction then merge.
        if (firstLabel.kind === 'userAction') {
            return firstMustBeAppliedRule(firstLabel, secondLabel, true);
        }

        if (secondLabel.kind === 'userAction') {
            return firstMustBeAppliedRule(secondLabel, firstLabel, true);
        }

        return mergeLabelsRule(firstLabel, secondLabel);
    }
};

const applyRules = (labels: InternalLabel[], topLevel = true): void => {
    const addedLabels: InternalLabel[] = [];
    const removedLabels = new Set<InternalLabel>();

    firstLoop: for (let i = 0; i < labels.length; i++) {
        const firstLabel = labels[i];
        if (removedLabels.has(firstLabel)) {
            continue;
        }
        for (let j = i + 1; j < labels.length; j++) {
            const secondLabel = labels[j];
            if (removedLabels.has(secondLabel)) {
                continue;
            }

            let rulesRet: RulesReturn;
            const overlap = doLabelsOverlap(firstLabel, secondLabel, label => ({
                start: label.originalLabel.start,
                end: label.originalLabel.end
            }));

            if (
                (firstLabel.originalLabel.entityId === NEGATIVE_ENTITY_ID || secondLabel.originalLabel.entityId === NEGATIVE_ENTITY_ID) &&
                overlap
            ) {
                rulesRet = innerLevelsRules(firstLabel, secondLabel);
            } else if (
                topLevel &&
                overlap &&
                (firstLabel.originalLabel.entityId === secondLabel.originalLabel.entityId ||
                    (firstLabel.hierarchicalParentId &&
                        secondLabel.hierarchicalParentId &&
                        firstLabel.hierarchicalParentId === secondLabel.hierarchicalParentId))
            ) {
                rulesRet = topLevelRules(firstLabel, secondLabel);
            } else if (!topLevel && overlap) {
                rulesRet = innerLevelsRules(firstLabel, secondLabel);
            }

            if (rulesRet) {
                labels.push(...rulesRet.addedLabels);
                const { removedLabel } = rulesRet;
                if (removedLabel) {
                    removedLabels.add(removedLabel);
                    if (removedLabel === firstLabel) {
                        continue firstLoop;
                    } else if (removedLabel === secondLabel) {
                        continue;
                    }
                }
            }
        }
    }

    removedLabels.forEach(label => {
        remove(labels, label);
    });
    labels.push(...addedLabels);
};

const addLabel = (label: InternalLabel, schema: EntityCollection, labels: InternalLabel[]): void => {
    const ancestorLabels = createAncestorLabels(schema, label);
    assignChildren(ancestorLabels, schema);
    labels.push(...ancestorLabels);
};

const removeLabel = (label: InternalLabel, schema: EntityCollection, labels: InternalLabel[]): boolean => {
    const deletePath = getSchemaPath(schema, label.originalLabel.entityId.toString());
    let children = labels;

    while (deletePath.length > 1) {
        const parentId = deletePath.pop();
        const foundLabel = children.find(
            ({ originalLabel: { entityId, start, end } }) =>
                entityId === parentId && start <= label.originalLabel.start && end >= label.originalLabel.end
        );
        if (!foundLabel) {
            return false;
        }

        children = foundLabel.children;
    }

    const index = children.findIndex(
        ({ originalLabel: { entityId, start, end } }) =>
            entityId === label.originalLabel.entityId && start === label.originalLabel.start && end === label.originalLabel.end
    );

    if (index === -1) {
        return false;
    }

    children.splice(index, 1);

    return true;
};

export const labelingInteractions = (
    state: LabelingInteractionsState,
    action: LabelingInteractionsAction,
    { mutualExclusiveTopLevel } = { mutualExclusiveTopLevel: false }
): LabelingInteractionsReturn => {
    const { labels, entities } = state;
    const schema = buildEntityCollection(entities);
    let internalLabels = labels.map(toInternalLabel('existing'));
    assignChildren(internalLabels, schema);

    switch (action.name) {
        case 'AddLabel': {
            const { label } = action;

            const internalLabel = toInternalLabel('userAction')(label);
            addLabel(internalLabel, schema, internalLabels);
            break;
        }
        case 'RemoveLabels': {
            const { labels: labelsToBeRemoved } = action;

            labelsToBeRemoved.forEach(label => {
                const internalLabel = toInternalLabel('userAction')(label);
                const isRemoved = removeLabel(internalLabel, schema, internalLabels);
                if (!isRemoved) {
                    throw new Error('Can not delete non existent label');
                }
            });
            break;
        }
        case 'ConfirmPredictions': {
            const { labels: labelsToBeAdded } = action;

            const newLabels = labelsToBeAdded.map(label => toInternalLabel('userAction')(label));
            assignChildren(newLabels, schema);
            newLabels.map(label => addLabel(label, schema, internalLabels));
            break;
        }
        default: {
            throw new Error(`Labeling Action(${action.name}) is not supported.`);
        }
    }

    attachHierarchicalParentIds(internalLabels, entities);
    applyRules(internalLabels, !mutualExclusiveTopLevel);

    internalLabels = internalLabels.filter(label => label.originalLabel.entityId !== NEGATIVE_ENTITY_ID);

    return { labels: toInteractionsLabel(internalLabels) };
};
